import { SetStateAction } from 'react';
import { IRecordModel, JourneyComments } from '../../store/_api/TypeDefs';
import { IJourneyFilterState } from './JourneyListFilterReducer';

/**
 * Represents the state of a journey list item.
 */
export interface JourneyListState {
  /**
    * The name of the journey.
    * @type {string}
    */
  journeyName: string;
  /**
   * The history of journey status comments.
   * @type {JourneyComments[]|undefined}
   */
  journeyStatusHistory?: JourneyComments[];
  /**
   * The status of the journey.
   * @type {string}
   */
  status: string;
  /**
   * The category of the journey.
   * @type {string}
   */
  journeyCategory: string;
  /**
   * The version of the journey.
   * @type {number}
   */
  version: number;
  /**
   * The user who last updated the journey.
   * @type {string}
   */
  updatedBy: string;
  /**
   * The timestamp when the journey was last updated.
   * @type {string}
   */
  updatedTimestamp: string;
}

/**
 * Represents the state of data loading.
 */
export enum DataLoadingState {
  /**
   * The data is currently being loaded.
   */
  LOADING = 'LOADING',

  /**
   * The data loading has completed successfully.
   */
  COMPLETED = 'COMPLETED',

  /**
   * The data loading has failed.
   */
  FAILED = 'FAILED'
}

/**
 * Represents the sorting order for API modification.
 */
export enum ApiModifySort {
  /**
   * Sort in ascending order.
   */
  ASC = 'ASC',

  /**
   * Sort in descending order.
   */
  DESC = 'DESC'
}

/**
 * Contains labels used in the application.
 */
export const Labels = {
  /**
   * Label for the journey name.
   */
  JOURNEY_NAME: 'journeyName',

  /**
   * Label for the journey status.
   */
  JOURNEY_STATUS: 'status',

  /**
   * Label for the group name.
   */
  GROUP_NAME: 'groupName',

  /**
   * Label for the last modified timestamp.
   */
  LAST_MODIFIED: 'updatedTimestamp',

  /**
   * Label for the updated by user.
   */
  UPDATED_BY: 'updatedBy',

  /**
   * Label for the action button.
   */
  ACTION_BTN: 'actionBtn',

  /**
   * Label for the active journey status.
   */
  JOURNEY_STATUS_ACTIVE: 'active',

  /**
   * Label for the inactive journey status.
   */
  JOURNEY_STATUS_INACTIVE: 'inactive',

  /**
   * Label for the draft journey status.
   */
  JOURNEY_STATUS_DRAFT: 'draft',

  /**
   * Label for the returned journey status.
   */
  JOURNEY_STATUS_RETURNED: 'returned',

  /**
   * Label for the pending approval journey status.
   */
  JOURNEY_STATUS_PENDING_APPROVAL: 'pending_approval',
};

/**
 * Contains constant values for export configuration.
 */
export const exportConfigConst = {
  /**
   * Text for the Content-Disposition header.
   */
  contentDisposition_text: 'Content-Disposition',

  /**
   * Text for the download action.
   */
  download_text: 'download'
};

/**
 * Contains constant values for the journey list.
 */
export const journeyListConst = {
  /**
   * Label for the search functionality.
   */
  lbl_search: 'search'
};

/**
 * Contains constant value for the default journey list status.
 */
export const defaultJourneyListStatus = 'Active,Draft,Returned,Pending_Approval';
export const defaultJourneyListStatusArray: string[] = ['Active', 'Draft', 'Returned', 'Pending_Approval'];

/**
 * Represents the possible statuses for a journey.
 */
export enum JourneyStatus {
  /**
   * Active journey status.
   */
  dropdown_active = 'Active',

  /**
   * Inactive journey status.
   */
  dropdown_inactive = 'Inactive',

  /**
   * Draft journey status.
   */
  dropdown_draft = 'Draft',

  /**
   * Returned journey status.
   */
  dropdown_returned = 'Returned',

  /**
   * Pending approval journey status (label).
   */
  dropdown_pending_approval_label = 'Pending Approval',

  /**
   * Pending approval journey status (value).
   */
  dropdown_pending_approval_value = 'Pending_Approval',
}

/**
 * Represents the footer options for a list of journeys.
 */
interface FooterObject {
  /**
   * The type of the footer.
   * @type {string}
   */
  footerType: string;

  /**
   * The options for the footer.
   * @type {object}
   */
  footerOptions: {
    /**
     * The number of items per page.
     * @type {number}
     */
    itemsPerPage: number;

    /**
     * The current page number.
     * @type {number}
     */
    currentPageNumber: number;

    /**
     * The total number of pages.
     * @type {number}
     */
    numberOfPages: number;

    /**
     * Callback function for when the current page number changes.
     * @type {(pageNumber: number) => void}
     */
    onCurrentPageNumberChange: (pageNumber: number) => void;

    /**
     * Callback function for when the number of items per page changes.
     * @type {(itemNumber: number) => void}
     */
    onItemsPerPageChange: (itemNumber: number) => void;
  };
}

/**
 * Represents the props for the ListJourneys component.
 */
export interface ListJourneysProps {
  /**
   * The status sort value.
   * @type {string | undefined}
   */
  statusSort: string | undefined;

  /**
   * Callback function to set the status sort value.
   * @type {(arg: string | undefined) => void}
   */
  setStatusSort: (arg: string | undefined) => void;

  /**
   * The last modified sort value.
   * @type {string}
   */
  lastModifiedSort: string;

  /**
   * Callback function to set the last modified sort value.
   * @type {(arg: string) => void}
   */
  setLastModifiedSort: (arg: string) => void;

  /**
   * The updated by sort value.
   * @type {string | undefined}
   */
  updatedBySort: string | undefined;

  /**
   * Callback function to set the updated by sort value.
   * @type {(arg: string | undefined) => void}
   */
  setUpdatedBySort: (arg: string | undefined) => void;

  /**
   * The sorted name value.
   * @type {string}
   */
  sortedName: string;

  /**
   * Callback function to set the sorted name value.
   * @type {(arg: string) => void}
   */
  setSortedName: (arg: string) => void;

  /**
   * The journey name sort value.
   * @type {string | undefined}
   */
  journeyNameSort: string | undefined;

  /**
   * Callback function to set the journey name sort value.
   * @type {(arg: string | undefined) => void}
   */
  setJourneyNameSort: (arg: string | undefined) => void;

  /**
   * The selected journey list.
   * @type {string[]}
   */
  selectedJourneyList: IRecordModel[];

  /**
   * Callback function for handling click events.
   * @type {(name: React.ChangeEvent<HTMLInputElement>) => void}
   */
  handleClick: (name: React.ChangeEvent<HTMLInputElement>, version: number) => void;

  /**
   * The sorted order value.
   * @type {string}
   */
  sortedOrder: string;

  /**
   * Callback function to set the sorted order value.
   * @type {(arg: SetStateAction<ApiModifySort>) => void}
   */
  setSortedOrder: (arg: SetStateAction<ApiModifySort>) => void;

  /**
   * Callback function to set the sort parameter value.
   * @type {(arg: string) => void}
   */
  setSortParameter: (arg: string) => void;

  /**
   * The query parameters.
   * @type {{ [key: string]: string }}
   */
  queryParams: { [key: string]: string };

  /**
   * Callback function to set the query parameters.
   * @type {(arg: SetStateAction<{ [key: string]: string; }>) => void}
   */
  setQueryParams: (arg: SetStateAction<{ [key: string]: string; }>) => void;

  /**
   * Callback function for handling select all events.
   * @type {() => void}
   */
  handleSelectAll: () => void;

  /**
   * Indicates whether all journeys are checked.
   * @type {boolean}
   */
  isCheckAllJourneys: boolean;

  /**
   * The footer object.
   * @type {FooterObject}
   */
  footerObj: FooterObject;

  /**
   * The list of journeys.
   * @type {JourneyListState[]}
   */
  journeyList: JourneyListState[];

  /**
   * Callback function for viewing a journey.
   * @type {(journeyName: string, currStatus: string, versionNum: string) => void}
   */
  viewJourney: (journeyName: string, versionNum: string) => void;

  /**
   * Callback function for editing a journey.
   * @type {(journeyName: string, currStatus: string, versionNum: string) => void}
   */
  editJourney: (journeyName: string, currStatus: string, versionNum: string) => void;
  /**
   * Callback function for deleting a journey.
   */
  onDeleteSuccess: () => void
}

export const parseSortString = (val?: string) => {
  if (!val) return { sort: '', type: '' };
  const [sort, type] = val.split(',');
  return { sort: sort ?? '', type: type ?? '' };
};

/**
 *
 * @method getFilterParams
 * @description Gets the current filter parameters.
 * If the search url is present, it will return the search url parameters.
 * Otherwise, it will return the current filters from the redux store.
 * @param search - The search url
 * @param filterState - The current filter state
 * @returns The filter parameters
 */
export const getFilterParams = (search: string, filterState: IJourneyFilterState) => {
  const params = new URLSearchParams(search);
  const parsedParams = {
    status: params.get('status') ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
    journeyName: params.get('journeyName') ?? '',
    fromDate: params.get('fromDate') ?? '',
    toDate: params.get('toDate') ?? '',
    sort: params.get('sort') ?? '',
    type: params.get('type') ?? ''
  };

  const hasUrlFilter = parsedParams.status || parsedParams.journeyName || parsedParams.fromDate || parsedParams.toDate || parsedParams.sort || parsedParams.type;
  if (hasUrlFilter) {
    const { sort: persistedSort, type: persistedType } = parseSortString(filterState.sort);
    return {
      ...parsedParams,
      sort: parsedParams.sort || persistedSort,
      type: parsedParams.type || persistedType
    };
  }

  const { sort, type } = parseSortString(filterState.sort);
  return {
    status: filterState.status ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
    journeyName: filterState.searchText ?? '',
    fromDate: String(filterState.fromDate ?? ''),
    toDate: String(filterState.toDate ?? ''),
    sort,
    type
  };
};
